import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { Adapt } from './lib/adapt.js';
import { SceneClock } from './lib/scene-clock.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';
import { getHersheySans, getHersheyFont } from '../hershey-font.js';
import { FREEZE_TIME_S } from '../debug-capture.js';
import { vdjColor, vdjBeatChroma, VdjPost, VdjComfort, VDJ_PRESETS, VDJ_DROP_FLASH } from './lib/vdjLook.js';

// Contrat du rig partagé : interdit. Un quad plein écran, un tampon de peinture
// privé, deux niveaux de lueur, un maillage instancié de têtes de coulures.
const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'blacklight',
  family: 'shader',
  danceability: 0.88,
  energyLevel: 0.84,
  bpmMin: 95,
  bpmMax: 175,
  movement: 0.75,
  tonality: 0.45,
  enabled: true
};

// Blacklight : une soirée à la lumière noire. Sur un grand mur noir mat, de la
// peinture FLUORESCENTE est projetée en rythme - éclaboussures, giclées,
// coulures, empreintes de mains, traits de doigts - qui brille en rose, vert,
// jaune, orange, bleu UV. Le mur se couvre jusqu'au chaos, puis un rouleau
// noir passe en un temps et efface tout. House, EDM, hip-hop, techno, bass
// music. Règles R1-R19 de kiosk-app/CLAUDE.md (+ R20 horloge de scène,
// R21 trait central, R22 tampon sans dérive), preset party de lib/vdjLook.js.
//
// LE TAMPON DE PEINTURE (R22) : une cible RGBA8 à la taille de la cible de
// rendu, jamais relue, jamais atténuée : on n'y fait que PEINDRE par-dessus
// (mélange normal, la dernière couche recouvre, aucun mélange boueux). Tout ce
// qui s'y dessine est une PRIMITIVE écrite par le CPU dans un anneau de 4096
// (texture flottante 128 x 96, 3 texels par primitive : position et
// direction | rayons, instant d'apparition, type | couleur, graine) au moment
// de l'événement musical, jamais par image. Chaque image, UN draw instancié
// de 4096 quads ne garde que les primitives dont l'instant d'apparition tombe
// dans la fenêtre (image précédente, image courante] : chacune est peinte une
// seule fois, à son heure, et les autres se replient en un point. Types :
//   TACHE    la tache principale d'une éclaboussure : rayon modulé par 7
//            harmoniques et 8 pointes (couronne, 4 vers l'avant), étirée vers la direction du
//            jet ; graine propre, aucune forme répétée
//   GOUTTE   ellipse étirée dans sa direction de vol (20 à 60 par tache,
//            atterrissant 10 à 90 ms après l'impact, surtout vers l'avant)
//   TRAIT    capsule effilée : traînées, giclées, coulures, traits de doigts
//   PINCEAU  capsule à poils : le titre et l'artiste
//   MAIN     paume + quatre doigts + pouce, impression lacunaire
//   RECT     noir : le rouleau
// Bords nets : couverture par la distance signée sur fwidth (1 px), un bord
// de 1,5 à 4,5 px plus saturé et plus lumineux que l'intérieur (épaisseur de
// peinture) ; la frange latérale seulement sur les traits, pour que les
// jointures d'un trait en morceaux ne fassent pas d'anneaux.
//
// LA LUMIÈRE NOIRE : la peinture ÉMET ; le mur est presque noir (0,011,
// 0,006, 0,020) avec un grain fin et une tache violette sous les tubes. Une
// lueur fluo propre à la peinture vient de deux réductions du tampon (1/4,
// 1/16, 4 lectures bilinéaires chacune), recalculées seulement les images où
// l'on a peint, sous le seuil du bloom ; le bloom partagé (seuil 0,78) ne
// prend que les bords des jaunes, verts et aquas. Deux tubes UV en haut de
// l'écran (profil R1 : cœur 2 px, gaine 2-6 px, halo exp(-d/10) x 0,35).
// Au stroboscope des montées, les tubes s'éteignent entre les temps et la
// peinture respire avec eux (70 % -> 100 %), au plus un flash par temps (R10).
//
// LE RYTHME (party, R9) :
//   TEMPS   une éclaboussure (direction, taille, couleur variées), placée au
//           mieux loin des précédentes ; en début de scène, jamais au centre
//           exact ni sur le titre
//   MESURE  une giclée en travers (Bézier cubique, 72 morceaux en ~0,3 s,
//           perles et gouttelettes) ; 1 à 3 coulures partent des taches
//           récentes (loi y0 - L(1 - e^(-t/tau)), tête en gouttelette dessinée
//           par-dessus l'écran, R15, goutte finale écrite dans le tampon) ;
//           parfois une main ou des traits de doigts
//   PHRASE  palette fluo suivante (3 couleurs dominantes, écart de teinte
//           >= 0,29, la première choisie près de la teinte de la pochette)
//   MONTÉE  éclaboussures plus petites, sur les croches ; stroboscope UV
//   DROP    huit éclaboussures simultanées depuis le centre, flash UV
// Le rouleau n'efface jamais un drop : il attend 4 mesures après lui ; une
// montée en fin de scène l'appelle tout de suite (mur propre pour le drop).
// LA SCÈNE (R20) : SceneClock de 48 s. À la fin, le rouleau noir attend le
// temps suivant et passe en UN temps de gauche à droite (corps noir, arête
// éclairée en violet, R13) ; derrière lui des bandes noires sont peintes
// dans le tampon, puis le tampon est vidé EXACTEMENT (clear) à sa sortie :
// rien ne survit, rien ne dérive. Les primitives encore en attente sont
// annulées au départ du rouleau. Le titre (Hershey sans, capitales) et
// l'artiste (Hershey script) sont écrits au pinceau en trait central (R21)
// sous le centre de composition au début de chaque scène, protégés des
// éclaboussures pendant la première moitié de la scène. Un changement de
// morceau appelle le rouleau et ouvre une scène.
//
// Ouverture : la scène démarre à une phase tirée (0,37 sous ?freezetime) et
// le mur est pré-rempli d'autant (titre, taches, giclées, coulures finies)
// pendant les premières images, pour ne jamais ouvrir sur un mur vide.
// Replis : sans BPM, le temps d'Adapt (onsets) ; sans pochette, palettes fixes ;
// sans titre, pas d'écriture ; en pause, plus rien n'est peint, l'image tient.
// Tier 0 : sans coulures.
//
// CONFORT (R10) : l'affichage rendu en 32 x 32 toutes les 0,5 s, gain par
// VdjComfort nourri des seules mesures fraîches.
//
// Debug : ?blsplat=1 une éclaboussure à chaque demi-temps, quelle que soit
// l'énergie ; ?bldrip=1 des coulures à chaque temps ; ?blwipe=1 le rouleau
// toutes les deux mesures.

const PW = 128, PROWS = 32, NPRIM = PW * PROWS;            // anneau de primitives
const TY = { BLOB: 0, DROP: 1, CAP: 2, BRUSH: 3, HAND: 4, RECT: 5 };
const MAXD = 24;                                           // coulures en vol
const SCENE_S = 48;
const ROLLER_W = 0.22;                                     // unités de hauteur (l'écran fait 2)
const TUBE_Y = 0.955, TUBE_IN = 0.78, TUBE_OUT = 0.2;
const C = {
  pink: [1.0, 0.0, 0.40], green: [0.12, 1.0, 0.0], blue: [0.04, 0.13, 1.0],
  orange: [1.0, 0.22, 0.0], violet: [0.45, 0.0, 1.0], yellow: [1.0, 0.80, 0.0], aqua: [0.0, 1.0, 0.62]
};
// trois couleurs par palette, écarts de teinte 0,28 à 0,40 (R4) ; canaux mineurs à
// zéro : l'encodage sRGB relève tout ce qui n'est pas nul et pâlit la couleur
const PALETTES = [[C.pink, C.green, C.blue], [C.orange, C.aqua, C.violet], [C.yellow, C.aqua, C.violet]];
const PAL_HUES = [[0.933, 0.313, 0.651], [0.037, 0.437, 0.742], [0.133, 0.437, 0.742]];

function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function urlParam(name) {
  if (typeof location === 'undefined') return null;
  return new URLSearchParams(location.search).get(name);
}
function mulberry(seed) {
  let s = seed >>> 0;
  return function () {
    s = (s + 0x6D2B79F5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
function plain(s) { return (s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/\s+/g, ' ').trim(); }

const QUAD_VERTEX = `
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy * 2.0, 0.0, 1.0); }
`;

// --- la peinture : un draw instancié de 4096 primitives ------------------------
// precision highp (exception documentée) : positions en unités de hauteur au
// pixel près et instants en secondes depuis le début.
const PAINT_VERTEX = `
  precision highp float;
  attribute float aIdx;
  uniform sampler2D uPrims;
  uniform float uT0;
  uniform float uT1;
  uniform float uAspect;
  uniform float uPx;
  varying vec2 vQ;
  varying vec4 vP;
  varying vec4 vC;
  vec4 prim(float row) {
    float x = mod(aIdx, ${PW}.0);
    float y = floor(aIdx / ${PW}.0) * 3.0 + row;
    return texture2D(uPrims, vec2((x + 0.5) / ${PW}.0, (y + 0.5) / ${PROWS * 3}.0));
  }
  void main() {
    vec4 A = prim(0.0), B = prim(1.0), Cc = prim(2.0);
    vC = Cc; vQ = vec2(0.0); vP = vec4(0.0);
    if (!(B.z > uT0 && B.z <= uT1)) { gl_Position = vec4(2.0, 2.0, 2.0, 1.0); return; }
    float type = B.w;
    vec2 c = A.xy, u = vec2(1.0, 0.0), hs;
    float pad = 2.5 * uPx, hl = 0.0;
    vP = vec4(B.x, B.y, 0.0, type);
    if (type < 0.5) { u = A.zw; hs = vec2(B.x * (1.0 + B.y + 1.4) + pad); }
    else if (type < 1.5) { u = A.zw; hs = vec2(B.x * (1.0 + B.y) + pad, B.x + pad); }
    else if (type < 3.5) {
      vec2 d = A.zw - A.xy; float L = length(d);
      u = L > 1e-6 ? d / L : vec2(1.0, 0.0);
      c = 0.5 * (A.xy + A.zw); hl = 0.5 * L;
      float r = max(B.x, B.y);
      hs = vec2(hl + r + pad, r + pad);
      vP.z = hl;
    }
    else if (type < 4.5) { u = A.zw; hs = vec2(B.x * 0.62 + pad); }
    else { hs = A.zw + pad; vP.xy = A.zw; }
    vec2 v = vec2(-u.y, u.x);
    vec2 q = position.xy * 2.0 * hs;
    vec2 w = c + u * q.x + v * q.y;
    vQ = q;
    gl_Position = vec4(w.x / uAspect, w.y, 0.0, 1.0);
  }
`;

const PAINT_FRAGMENT = `
  precision highp float;
  varying vec2 vQ;
  varying vec4 vP;
  varying vec4 vC;
  float h11(float n) { return fract(sin(n * 91.3458) * 47453.5453); }
  float h21(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
  float vnoise(vec2 p) {
    vec2 i = floor(p), f = fract(p); f = f * f * (3.0 - 2.0 * f);
    return mix(mix(h21(i), h21(i + vec2(1.0, 0.0)), f.x), mix(h21(i + vec2(0.0, 1.0)), h21(i + vec2(1.0, 1.0)), f.x), f.y);
  }
  float sdSeg(vec2 p, vec2 a, vec2 b, float r) {
    vec2 pa = p - a, ba = b - a;
    float h = clamp(dot(pa, ba) / dot(ba, ba), 0.0, 1.0);
    return length(pa - ba * h) - r;
  }
  float sdBox(vec2 p, vec2 b, float r) {
    vec2 d = abs(p) - b + r;
    return length(max(d, 0.0)) + min(max(d.x, d.y), 0.0) - r;
  }
  // la tache : 7 harmoniques entières (continues à la couture de atan) et 5
  // pointes, trois vers l'avant (le jet), deux n'importe où
  float sdBlob(vec2 q, float r0, float el, float sd) {
    float ang = atan(q.y, q.x);
    float c = max(cos(ang), 0.0);
    float R = 1.0 + el * c * c;
    for (int k = 0; k < 7; k++) {
      float fk = float(k);
      float n = 2.0 + fk + floor(h11(sd * 13.0 + fk) * 2.0);
      R += (0.10 / (1.0 + 0.45 * fk)) * (h11(sd * 7.0 + fk * 3.1) * 1.2 - 0.2) * sin(n * ang + 6.2832 * h11(sd * 3.0 + fk * 5.7));
    }
    for (int j = 0; j < 8; j++) {
      float fj = float(j);
      float phi = j < 4 ? (h11(sd * 11.0 + fj) - 0.5) * 2.4 : h11(sd * 17.0 + fj) * 6.2832;
      float cc = max(cos(ang - phi), 0.0);
      R += (0.15 + (j < 4 ? 0.85 : 0.45) * h11(sd * 19.0 + fj)) * pow(cc, 30.0 + 90.0 * h11(sd * 23.0 + fj));
    }
    return length(q) - r0 * R;
  }
  float sdHand(vec2 q, float s, float sd) {
    q /= s;
    if (h11(sd * 5.0) < 0.5) q.y = -q.y;          // main gauche ou droite
    float d = sdBox(q - vec2(-0.16, 0.0), vec2(0.21, 0.19), 0.08);
    float spread = 0.6 + 0.9 * h11(sd * 7.0);
    for (int i = 0; i < 4; i++) {
      float fi = float(i);
      float ang = (fi - 1.5) * 0.09 * spread;
      float len = (0.27 + 0.10 * sin((fi + 0.4) * 0.95)) * (0.92 + 0.16 * h11(sd * 3.0 + fi));
      vec2 a = vec2(0.02, -0.135 + 0.09 * fi);
      d = min(d, sdSeg(q, a, a + len * vec2(cos(ang), sin(ang)), 0.048 - 0.004 * fi));
    }
    float ta = 0.8 + 0.4 * h11(sd * 9.0);
    d = min(d, sdSeg(q, vec2(-0.13, 0.16), vec2(-0.13, 0.16) + 0.24 * vec2(cos(ta), sin(ta)), 0.055));
    return d * s;
  }
  void main() {
    float type = vP.w, sd = vC.w;
    vec2 q = vQ;
    float f, rimF;
    if (type < 0.5) { f = sdBlob(q, vP.x, vP.y, sd); rimF = f; }
    else if (type < 1.5) { f = length(vec2(q.x / (1.0 + vP.y), q.y)) - vP.x; rimF = f; }
    else if (type < 3.5) {
      float hl = vP.z;
      float s = clamp((q.x + hl) / max(2.0 * hl, 1e-6), 0.0, 1.0);
      float r = mix(vP.x, vP.y, s);
      f = length(q - vec2(-hl + 2.0 * hl * s, 0.0)) - r;
      rimF = abs(q.y) - r;                          // frange latérale seulement
    }
    else if (type < 4.5) { f = sdHand(q, vP.x, sd); rimF = f; }
    else { vec2 d = abs(q) - vP.xy; f = max(d.x, d.y); rimF = f; }
    float fw = max(fwidth(f), 1e-6);
    float cov = clamp(0.5 - f / fw, 0.0, 1.0);
    if (cov <= 0.0) discard;
    vec3 col = vC.rgb;
    if (type < 4.5) {
      // l'épaisseur : un bord de peinture plus saturé et plus vif
      float inPx = -rimF / max(fwidth(rimF), 1e-6);
      float lum = dot(col, vec3(0.2126, 0.7152, 0.0722));
      vec3 inner = mix(col, vec3(lum), 0.03) * 0.80;
      col = mix(col, inner, smoothstep(1.5, 4.5, inPx));
    }
    if (type > 2.5 && type < 3.5) {
      // les poils du pinceau : des sillons le long du trait, constants par trait
      float v = q.y / max(0.5 * (vP.x + vP.y), 1e-5);
      col *= 0.80 + 0.20 * h11(floor((v + 1.0) * 4.0) + sd * 31.0);
    }
    if (type > 3.5 && type < 4.5) cov *= smoothstep(0.22, 0.55, vnoise(q / vP.x * 16.0 + sd * 40.0));
    gl_FragColor = vec4(col, cov);
  }
`;

// --- la lueur fluo : réduction par 4 (4 lectures bilinéaires = boîte 4 x 4) ---
const DOWN_FRAGMENT = `
  precision mediump float;
  uniform sampler2D uSrc;
  uniform vec2 uTexel;
  varying vec2 vUv;
  void main() {
    gl_FragColor = 0.25 * (texture2D(uSrc, vUv + uTexel * vec2(-1.0, -1.0)) + texture2D(uSrc, vUv + uTexel * vec2(1.0, -1.0))
                         + texture2D(uSrc, vUv + uTexel * vec2(-1.0, 1.0)) + texture2D(uSrc, vUv + uTexel * vec2(1.0, 1.0)));
  }
`;

// --- l'affichage : mur, peinture, lueur, tubes UV, rouleau ---------------------
const DISPLAY_FRAGMENT = `
  precision highp float;
  uniform sampler2D uPaint;
  uniform sampler2D uGlowA;
  uniform sampler2D uGlowB;
  uniform vec2 uRes;
  uniform float uUV;
  uniform float uGain;
  uniform float uRollerX;
  uniform vec2 uSafeCenter;
  uniform vec2 uSafeHalfSize;
  uniform float uSafeFeather;
  uniform float uZoom;              // the camera: >= 1 (never an edge), slow push + kick on the beat
  uniform vec2 uPan;                // in height units, bounded by the zoom's margin
  uniform vec4 uSpot[2];            // the moving UV lights: centre (wall units), radius, level
  uniform float uPulse;             // the fluorescence's breath on the beat
  varying vec2 vUv;
  const vec3 V = vec3(0.42, 0.12, 1.0);
  float h21(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
  float vnoise(vec2 p) {
    vec2 i = floor(p), f = fract(p); f = f * f * (3.0 - 2.0 * f);
    return mix(mix(h21(i), h21(i + vec2(1.0, 0.0)), f.x), mix(h21(i + vec2(0.0, 1.0)), h21(i + vec2(1.0, 1.0)), f.x), f.y);
  }
  float tubeD(vec2 p, float x0, float x1) { return length(vec2(p.x - clamp(p.x, x0, x1), p.y - ${TUBE_Y})); }
  void main() {
    float ppu = 0.5 * uRes.y * uZoom;               // pixels par unité de hauteur DU MUR (la caméra zoome)
    float asp = uRes.x / uRes.y;
    vec2 scr = vec2((vUv.x * 2.0 - 1.0) * asp, vUv.y * 2.0 - 1.0);   // l'écran (masque de pochette)
    // le mur vu par la caméra : tout ce qui est SUR le mur (peinture, tubes, rouleau) bouge ensemble
    vec2 sc = scr / uZoom + uPan;
    vec2 wuv = vec2(sc.x / asp, sc.y) * 0.5 + 0.5;
    float grain = h21(floor(wuv * uRes));
    float mott = 0.6 * vnoise(sc * 6.0) + 0.4 * vnoise(sc * 23.0);
    float on = clamp(uUV, 0.0, 1.4);
    float breath = 0.7 + 0.3 * on;
    // le mur : noir mat, grain fin, une tache violette sous les tubes
    float top = exp(-(1.0 - sc.y) * 2.5);
    vec3 wall = vec3(0.0035, 0.0018, 0.0062) * (0.7 + 0.3 * grain + 0.35 * mott) * (0.55 + 0.45 * min(on, 1.2)) * (1.0 + 1.5 * top);
    vec3 paint = texture2D(uPaint, wuv).rgb;
    float pm = max(max(paint.r, paint.g), paint.b);
    vec3 glow = texture2D(uGlowA, wuv).rgb * 0.14 + texture2D(uGlowB, wuv).rgb * 0.06;
    glow *= 1.0 - 0.75 * clamp(pm * 2.5, 0.0, 1.0);
    // les lumières noires mobiles : la peinture qu'elles balaient s'allume plus fort, le mur nu prend un voile violet
    float lit = 0.0;
    for (int i = 0; i < 2; i++) { vec2 d = (sc - uSpot[i].xy) / uSpot[i].z; lit += uSpot[i].w * exp(-dot(d, d)); }
    float fluo = (0.62 + 0.55 * lit) * uPulse;
    vec3 col = wall * (1.0 - clamp(pm * 4.0, 0.0, 1.0)) * (1.0 + 0.8 * lit) + (paint * (0.93 + 0.07 * grain) + glow) * breath * fluo;
    col += V * 0.0025 * lit * (1.0 - clamp(pm * 4.0, 0.0, 1.0));   // a faint violet where the lights pass: the wall stays black
    // les tubes UV (R1)
    // the tubes are fixtures of the room, in front of the wall: screen space (the camera's zoom would crop them out)
    float dT = min(tubeD(scr, -asp + ${TUBE_OUT}, -${TUBE_IN}), tubeD(scr, ${TUBE_IN}, asp - ${TUBE_OUT})) * 0.5 * uRes.y;
    col += vec3(0.92, 0.85, 1.0) * (1.0 - smoothstep(1.2, 2.2, dT)) * on;
    col += V * 0.8 * smoothstep(1.0, 2.2, dT) * (1.0 - smoothstep(4.0, 6.0, dT)) * (0.12 + 0.88 * on);
    col += V * 0.35 * exp(-dT / 10.0) * on;
    // le rouleau : corps noir, peluches à peine visibles, arête avant éclairée (R13)
    if (uRollerX > -9.0) {
      float xr = uRollerX - sc.x;
      if (xr > 0.0 && xr < ${ROLLER_W}) {
        col = vec3(0.006, 0.004, 0.010) * (0.5 + h21(floor(vec2(sc.x * ppu * 0.5, sc.y * ppu * 0.25)))) * (0.4 + 0.6 * on);
        float ePx = xr * ppu;
        col += V * 0.5 * (1.0 - smoothstep(1.5, 3.0, ePx)) * on;
        col += V * 0.18 * (1.0 - smoothstep(0.0, 1.0, abs(ePx - ${ROLLER_W} * ppu + 1.0))) * on;
      }
    }
    col *= uGain;
    vec2 qs = abs(scr - uSafeCenter) - uSafeHalfSize;
    float sdz = length(max(qs, 0.0)) + min(max(qs.x, qs.y), 0.0);
    col *= mix(0.40, 1.0, smoothstep(0.0, uSafeFeather, sdz));
    gl_FragColor = vec4(col, 1.0);
  }
`;

// --- les têtes de coulure : par-dessus l'écran (R15), jamais dans le tampon ----
const HEAD_VERTEX = `
  precision highp float;
  attribute float aSlot;
  uniform sampler2D uDrips;
  uniform float uTime;
  uniform float uAspect;
  uniform float uPx;
  uniform float uZoom;
  uniform vec2 uPan;
  varying vec2 vQ;
  varying float vR;
  varying vec3 vCol;
  varying vec2 vSc;
  vec4 slot(float row) { return texture2D(uDrips, vec2((aSlot + 0.5) / ${MAXD}.0, (row + 0.5) / 3.0)); }
  void main() {
    vec4 A = slot(0.0), B = slot(1.0), Cc = slot(2.0);
    vCol = Cc.rgb; vQ = vec2(0.0); vR = 1.0; vSc = vec2(0.0);
    if (!(uTime > B.x && uTime < B.y)) { gl_Position = vec4(2.0, 2.0, 2.0, 1.0); return; }
    float y = A.y - A.z * (1.0 - exp(-(uTime - B.x) / A.w));
    float r = B.z * 1.7;
    vec2 c = vec2(A.x, y - B.z * 0.4);
    float h = r * 1.6 + 2.5 * uPx / uZoom;
    vQ = position.xy * 2.0 * h; vR = r;
    vec2 w = c + vQ;
    vec2 ws = (w - uPan) * uZoom;          // the camera, as on the wall
    vSc = ws;
    gl_Position = vec4(ws.x / uAspect, ws.y, 0.0, 1.0);
  }
`;
const HEAD_FRAGMENT = `
  precision highp float;
  uniform float uHeadGain;
  uniform vec2 uSafeCenter;
  uniform vec2 uSafeHalfSize;
  uniform float uSafeFeather;
  varying vec2 vQ;
  varying float vR;
  varying vec3 vCol;
  varying vec2 vSc;
  void main() {
    // goutte : un disque prolongé vers le haut, là où le filet la tient
    vec2 q = vQ;
    float f = length(vec2(q.x, q.y > 0.0 ? q.y * 0.6 : q.y)) - vR;
    float fw = max(fwidth(f), 1e-6);
    float cov = clamp(0.5 - f / fw, 0.0, 1.0);
    if (cov <= 0.0) discard;
    float inPx = -f / fw;
    float lum = dot(vCol, vec3(0.2126, 0.7152, 0.0722));
    vec3 col = mix(vCol, mix(vCol, vec3(lum), 0.03) * 0.80, smoothstep(1.5, 4.5, inPx));
    vec2 qs = abs(vSc - uSafeCenter) - uSafeHalfSize;
    float sdz = length(max(qs, 0.0)) + min(max(qs.x, qs.y), 0.0);
    gl_FragColor = vec4(col * uHeadGain * mix(0.40, 1.0, smoothstep(0.0, uSafeFeather, sdz)), cov);
  }
`;

export class BlacklightVisualizer extends Visualizer {
  constructor(options) {
    super();
    const o = options || {};
    this._tier = o.qualityTier != null ? o.qualityTier : 1;
    this._dbgSplat = urlParam('blsplat') === '1';
    this._dbgDrip = urlParam('bldrip') === '1';
    this._dbgWipe = urlParam('blwipe') === '1';
    this._drips = this._tier > 0;
    this._post = new VdjPost({ bloomStrength: 0.55, bloomThreshold: 0.78, grain: 0.012, vignette: 0.30, toneMapping: THREE.NoToneMapping, toneMappingExposure: 1 });
    this._comfort = new VdjComfort(VDJ_PRESETS.party.comfort);
  }

  get postProcessing() { return this._post.value; }

  init(scene) {
    this._scene = scene;
    this._adapt = new Adapt();
    this._clock = new SceneClock({ period: SCENE_S, start: 'random' });
    this._rnd = mulberry(FREEZE_TIME_S != null ? 1234 : (Math.random() * 4294967296) >>> 0);
    this._ortho = new THREE.OrthographicCamera(-0.5, 0.5, 0.5, -0.5, 0, 1);

    // l'anneau de primitives
    this._pd = new Float32Array(PW * PROWS * 3 * 4);
    for (let i = 0; i < NPRIM; i++) this._pd[this._row(i, 1) + 2] = -1;
    this._ptex = new THREE.DataTexture(this._pd, PW, PROWS * 3, THREE.RGBAFormat, THREE.FloatType);
    this._ptex.minFilter = this._ptex.magFilter = THREE.NearestFilter; this._ptex.generateMipmaps = false; this._ptex.needsUpdate = true;
    this._next = 0; this._maxT = -1; this._pdirty = false; this._written = 0;

    const plane = new THREE.PlaneGeometry(1, 1);
    const pg = new THREE.InstancedBufferGeometry();
    pg.index = plane.index; pg.setAttribute('position', plane.getAttribute('position'));
    const idx = new Float32Array(NPRIM); for (let i = 0; i < NPRIM; i++) idx[i] = i;
    pg.setAttribute('aIdx', new THREE.InstancedBufferAttribute(idx, 1));
    pg.instanceCount = NPRIM;
    this._paintU = { uPrims: { value: this._ptex }, uT0: { value: 0 }, uT1: { value: 0 }, uAspect: { value: 16 / 9 }, uPx: { value: 2 / 1080 } };
    this._paintMesh = new THREE.Mesh(pg, new THREE.ShaderMaterial({ uniforms: this._paintU, vertexShader: PAINT_VERTEX, fragmentShader: PAINT_FRAGMENT, transparent: true, blending: THREE.NormalBlending, depthTest: false, depthWrite: false }));
    this._paintMesh.frustumCulled = false;
    this._paintScene = new THREE.Scene(); this._paintScene.add(this._paintMesh);

    this._downU = { uSrc: { value: null }, uTexel: { value: new THREE.Vector2() } };
    this._downMesh = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShaderMaterial({ uniforms: this._downU, vertexShader: QUAD_VERTEX, fragmentShader: DOWN_FRAGMENT, depthTest: false, depthWrite: false }));
    this._downMesh.frustumCulled = false;
    this._downScene = new THREE.Scene(); this._downScene.add(this._downMesh);
    this._paintRT = null; this._glowA = null; this._glowB = null; this._bw = 0; this._bh = 0;

    this._u = {
      uPaint: { value: null }, uGlowA: { value: null }, uGlowB: { value: null },
      uRes: { value: new THREE.Vector2(1920, 1080) }, uUV: { value: 1 }, uGain: { value: 1 }, uRollerX: { value: -99 },
      uZoom: { value: 1.04 }, uPan: { value: new THREE.Vector2() }, uSpot: { value: [new THREE.Vector4(-0.8, 0.2, 0.7, 0.6), new THREE.Vector4(0.8, -0.2, 0.7, 0.6)] }, uPulse: { value: 1 },
      uSafeCenter: safeZoneUniforms.uSafeCenter, uSafeHalfSize: safeZoneUniforms.uSafeHalfSize, uSafeFeather: safeZoneUniforms.uSafeFeather
    };
    this._quad = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShaderMaterial({ uniforms: this._u, vertexShader: QUAD_VERTEX, fragmentShader: DISPLAY_FRAGMENT, depthTest: false, depthWrite: false }));
    this._quad.frustumCulled = false; this._quad.renderOrder = -3000;
    const self = this;
    this._quad.onBeforeRender = function (renderer) { self._render(renderer); };
    scene.add(this._quad);

    // les têtes de coulure
    this._dd = new Float32Array(MAXD * 3 * 4);
    this._dtex = new THREE.DataTexture(this._dd, MAXD, 3, THREE.RGBAFormat, THREE.FloatType);
    this._dtex.minFilter = this._dtex.magFilter = THREE.NearestFilter; this._dtex.generateMipmaps = false; this._dtex.needsUpdate = true;
    const hg = new THREE.InstancedBufferGeometry();
    hg.index = plane.index; hg.setAttribute('position', plane.getAttribute('position'));
    const sl = new Float32Array(MAXD); for (let i = 0; i < MAXD; i++) sl[i] = i;
    hg.setAttribute('aSlot', new THREE.InstancedBufferAttribute(sl, 1));
    hg.instanceCount = MAXD;
    this._headU = {
      uDrips: { value: this._dtex }, uTime: { value: 0 }, uAspect: safeZoneUniforms.uAspect, uPx: this._paintU.uPx, uHeadGain: { value: 1 }, uZoom: null, uPan: null,
      uSafeCenter: safeZoneUniforms.uSafeCenter, uSafeHalfSize: safeZoneUniforms.uSafeHalfSize, uSafeFeather: safeZoneUniforms.uSafeFeather
    };
    this._headU.uZoom = this._u.uZoom; this._headU.uPan = this._u.uPan;   // the same camera as the wall
    this._heads = new THREE.Mesh(hg, new THREE.ShaderMaterial({ uniforms: this._headU, vertexShader: HEAD_VERTEX, fragmentShader: HEAD_FRAGMENT, transparent: true, blending: THREE.NormalBlending, depthTest: false, depthWrite: false }));
    this._heads.frustumCulled = false; this._heads.renderOrder = -2990; this._heads.visible = this._drips;
    scene.add(this._heads);
    this._dripSlot = 0;

    this._measScene = new THREE.Scene();
    const mq = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), this._quad.material);
    mq.frustumCulled = false; this._measScene.add(mq);
    this._measRT = new THREE.WebGLRenderTarget(32, 32, { type: THREE.UnsignedByteType, depthBuffer: false, minFilter: THREE.NearestFilter, magFilter: THREE.NearestFilter });
    this._measBuf = new Uint8Array(32 * 32 * 4);

    this._time = 0; this._drawnT = 0; this._beats = 0; this._bars = 0; this._beatAge = 9; this._halfDone = false;
    this._build = 0; this._dropAge = 99; this._phraseN = 0; this._palBase = 0; this._paletteRef = undefined;
    this._uvFlash = 0; this._uv = 1; this._gain = 1; this._lumAge = 0; this._lumMeasured = 0.2; this._lumFresh = false; this._paused = false;
    this._recent = [];           // centres des dernières éclaboussures (placement)
    this._blobs = [];            // taches récentes (sources de coulures)
    this._wipe = null; this._wipePending = false; this._wipeAsked = 0; this._clearAt = -1; this._needClear = true;
    this._title = ''; this._artist = ''; this._textKey = null; this._textBox = null; this._textDue = -1; this._textUntil = -1;
    this._started = false; this._aspect = 16 / 9;
    this._stats = { splats: 0, jets: 0, drips: 0, hands: 0, wipes: 0, texts: 0 };
  }

  // --- l'anneau --------------------------------------------------------------
  _row(i, row) { return ((Math.floor(i / PW) * 3 + row) * PW + (i % PW)) * 4; }

  _prim(type, ax, ay, bx, by, r0, r1, t, col, seed) {
    const i = this._next; this._next = (i + 1) % NPRIM;
    const d = this._pd;
    let o = this._row(i, 0); d[o] = ax; d[o + 1] = ay; d[o + 2] = bx; d[o + 3] = by;
    o = this._row(i, 1); d[o] = r0; d[o + 1] = r1; d[o + 2] = t; d[o + 3] = type;
    o = this._row(i, 2); d[o] = col[0]; d[o + 1] = col[1]; d[o + 2] = col[2]; d[o + 3] = seed != null ? seed : this._rnd() * 100;
    if (t > this._maxT) this._maxT = t;
    this._pdirty = true; this._written++;
  }

  // annule tout ce qui n'est pas encore peint (le rouleau part)
  _cancelPending(after) {
    for (let i = 0; i < NPRIM; i++) { const o = this._row(i, 1) + 2; if (this._pd[o] > after) this._pd[o] = -1; }
    for (let s = 0; s < MAXD; s++) { this._dd[(MAXD + s) * 4 + 1] = -1; }
    this._dtex.needsUpdate = true;
    this._pdirty = true;
  }

  // --- couleurs ----------------------------------------------------------------
  _applyPalette(pal) {
    // la première palette : celle qui contient la teinte la plus proche de la pochette
    if (pal && pal.vividColors && pal.vividColors.length) {
      const c = vdjColor(pal.vividColors[0], new THREE.Color(), VDJ_PRESETS.party.saturation);
      const hsl = {}; c.getHSL(hsl);
      let best = 0, bd = 9;
      PAL_HUES.forEach((hs, k) => hs.forEach((h) => { let d = Math.abs(h - hsl.h); d = Math.min(d, 1 - d); if (d < bd) { bd = d; best = k; } }));
      this._palBase = best;
    }
  }
  _palette() { return PALETTES[(this._palBase + this._phraseN) % PALETTES.length]; }
  // trois dominantes : la rotation de phrase en phrase change aussi l'ordre de poids
  _color() {
    const p = this._palette(), r = this._rnd(), rot = this._phraseN % 3;
    const k = r < 0.45 ? 0 : r < 0.78 ? 1 : 2;
    return p[(k + rot) % 3];
  }

  // --- les gestes --------------------------------------------------------------
  // le titre reste lisible : rien ne s'y pose tant qu'il est protégé (la
  // première moitié de la scène, au moins 12 s), tache gonflée de son rayon
  _protected(x, y, r) {
    const b = this._textBox, m = (r || 0) * 2.2;
    return !!(b && this._time < this._textUntil && x > b[0] - m && x < b[2] + m && y > b[1] - m && y < b[3] + m);
  }

  _place(r0, early) {
    const a = this._aspect, rnd = this._rnd;
    let best = null, bs = -1;
    for (let k = 0; k < 6; k++) {
      const x = (rnd() * 2 - 1) * a * 0.94, y = -0.94 + rnd() * 1.78;
      if (early && Math.hypot(x, y) < 0.5) continue;              // pas le centre exact en début de scène
      if (this._protected(x, y, r0)) continue;
      let m = 9;
      for (const p of this._recent) m = Math.min(m, Math.hypot(p[0] - x, p[1] - y));
      if (m > bs) { bs = m; best = [x, y]; }
    }
    return best || [a * 0.6 * (rnd() < 0.5 ? -1 : 1), 0.5 - rnd()];
  }

  _splat(x, y, r0, dir, col, t, nDrops) {
    const rnd = this._rnd, cd = Math.cos(dir), sd = Math.sin(dir);
    const el = 0.15 + 0.5 * rnd();
    // ordre d'écriture = ordre de peinture : tout ce qui tombe DANS la tache
    // (traînées, gouttes proches) est peint avant elle et recouvert, sinon
    // leurs bords plus vifs dessineraient des lignes à l'intérieur
    const later = [];
    const ns = 2 + Math.floor(4 * rnd());
    for (let k = 0; k < ns; k++) {
      const ang = dir + (rnd() - 0.5) * 1.1;
      const ca = Math.cos(ang), sa = Math.sin(ang);
      const from = r0 * 0.5, len = r0 * (1.0 + 1.6 * rnd());
      const w = r0 * (0.08 + 0.06 * rnd());
      this._prim(TY.CAP, x + ca * from, y + sa * from, x + ca * (from + len), y + sa * (from + len), w, w * 0.25, t, col);
      later.push([TY.DROP, x + ca * (from + len + w * 0.8), y + sa * (from + len + w * 0.8), ca, sa, w * 0.7, 0.5, t + 0.04]);
    }
    // les gouttelettes : surtout vers l'avant, atterrissent après l'impact
    const n = nDrops != null ? nDrops : 20 + Math.floor(40 * rnd());
    const inside = r0 * (1.0 + el + 0.35);
    for (let k = 0; k < n; k++) {
      const fwd = rnd() < 0.75;
      const ang = dir + (fwd ? (rnd() - 0.5) * 1.5 : rnd() * Math.PI * 2);
      const u = Math.pow(rnd(), 1.6);
      const dist = r0 * (1.05 + (fwd ? 2.6 : 1.0) * u);
      const rad = Math.max(0.0025, r0 * (0.03 + 0.12 * Math.pow(rnd(), 2.5)) * (1 - 0.45 * u));
      const e = fwd ? 0.2 + 1.3 * u * rnd() : 0.15;
      const q = [TY.DROP, x + Math.cos(ang) * dist, y + Math.sin(ang) * dist, Math.cos(ang), Math.sin(ang), rad, e, t + 0.01 + 0.08 * u];
      if (dist < inside) { q[7] = t; this._prim(...q, col); } else later.push(q);
    }
    this._prim(TY.BLOB, x, y, cd, sd, r0, el, t, col);
    for (const q of later) this._prim(...q, col);
    this._recent.push([x, y]); if (this._recent.length > 10) this._recent.shift();
    this._blobs.push({ x, y, r: r0, col, t }); if (this._blobs.length > 8) this._blobs.shift();
    this._stats.splats++;
  }

  _beatSplat(t, scale, early) {
    const rnd = this._rnd;
    const r0 = (0.05 + 0.08 * rnd()) * scale;
    const p = this._place(r0, early);
    this._splat(p[0], p[1], r0, rnd() * Math.PI * 2, this._color(), t);
  }

  _jet(t, beatS) {
    const a = this._aspect, rnd = this._rnd;
    const side = rnd() < 0.5 ? -1 : 1;
    const lowY = this._textBox && this._time < this._textUntil ? this._textBox[3] + 0.05 : -0.85;
    const yy = () => lowY + rnd() * (0.85 - lowY);
    const P0 = [side * a * (0.55 + 0.4 * rnd()), yy()], P3 = [-side * a * (0.35 + 0.55 * rnd()), yy()];
    const P1 = [P0[0] + (P3[0] - P0[0]) * 0.33, P0[1] + (rnd() - 0.5) * 1.2];
    const P2 = [P0[0] + (P3[0] - P0[0]) * 0.66, P3[1] + (rnd() - 0.5) * 1.2];
    const bez = (s) => {
      const m = 1 - s;
      return [m * m * m * P0[0] + 3 * m * m * s * P1[0] + 3 * m * s * s * P2[0] + s * s * s * P3[0],
        m * m * m * P0[1] + 3 * m * m * s * P1[1] + 3 * m * s * s * P2[1] + s * s * s * P3[1]];
    };
    const col = this._color();
    const dur = Math.max(0.18, Math.min(0.35, beatS * 0.6));
    const base = 0.013 + 0.011 * rnd();
    const ph1 = rnd() * 6.28, ph2 = rnd() * 6.28;
    const wid = (s) => base * Math.max(0.3, 0.7 + 0.5 * Math.sin(s * 23 + ph1) * Math.sin(s * 7.3 + ph2)) * (1 - 0.65 * s) * Math.min(1, 0.4 + s * 12);
    const N = 72;
    let prev = bez(0), pw = wid(0);
    for (let k = 1; k <= N; k++) {
      const s = k / N, p = bez(s), w = wid(s);
      this._prim(TY.CAP, prev[0], prev[1], p[0], p[1], pw, w, t + dur * s, col);
      prev = p; pw = w;
    }
    const m = 28 + Math.floor(24 * rnd());
    for (let k = 0; k < m; k++) {
      const s = rnd(), p = bez(s), q = bez(Math.min(1, s + 0.01));
      let tx = q[0] - p[0], ty = q[1] - p[1]; const l = Math.hypot(tx, ty) || 1; tx /= l; ty /= l;
      const off = (rnd() - 0.5) * 0.12 * (0.3 + s);
      this._prim(TY.DROP, p[0] - ty * off, p[1] + tx * off, tx, ty, base * (0.15 + 0.35 * rnd()), 0.5 + rnd(), t + dur * s + 0.01, col);
    }
    const e = bez(1), e0 = bez(0.98);
    const ang = Math.atan2(e[1] - e0[1], e[0] - e0[0]);
    this._splat(e[0], e[1], base * 2.6, ang, col, t + dur, 8 + Math.floor(8 * rnd()));
    this._stats.splats--; this._stats.jets++;
  }

  _drip(b, t, done) {
    const rnd = this._rnd;
    const x = b.x + (rnd() - 0.5) * b.r * 0.8, y0 = b.y - b.r * 0.55;
    const L = 0.12 + 0.35 * rnd(), tau = 1.2 + 1.5 * rnd(), w = 0.003 + 0.003 * rnd();
    const tEnd = t + 3 * tau;
    const yEnd = y0 - L * (1 - Math.exp(-3));
    const step = 0.006;
    let yPrev = y0;
    for (let y = y0 - step; y > yEnd; y -= step) {
      const k = (y0 - y) / L;
      const ty = done ? t + 0.001 : t - tau * Math.log(1 - k);
      const ww = w * (1 - 0.25 * k);
      this._prim(TY.CAP, x, yPrev, x, y, ww, ww, ty, b.col);
      yPrev = y;
    }
    this._prim(TY.DROP, x, yEnd - w * 0.4, 0, -1, w * 1.9, 0.3, done ? t + 0.001 : tEnd, b.col);
    if (!done) {
      const s = this._dripSlot = (this._dripSlot + 1) % MAXD, d = this._dd;
      d.set([x, y0, L, tau], s * 4);
      d.set([t, tEnd, w, 0], (MAXD + s) * 4);
      d.set([b.col[0], b.col[1], b.col[2], 0], (2 * MAXD + s) * 4);
      this._dtex.needsUpdate = true;
    }
    this._stats.drips++;
  }

  _hand(t) {
    const rnd = this._rnd, s = 0.28 + 0.1 * rnd();
    const p = this._place(s * 0.5, true);
    const ang = Math.PI / 2 + (rnd() - 0.5) * 1.2;
    this._prim(TY.HAND, p[0], p[1], Math.cos(ang), Math.sin(ang), s, 0, t, this._color());
    this._recent.push(p); if (this._recent.length > 10) this._recent.shift();
    this._stats.hands++;
  }

  // traits de doigts : quatre traits parallèles tirés en 1/2 temps
  _fingers(t, beatS) {
    const rnd = this._rnd;
    const p = this._place(0.2, true), col = this._color();
    const ang = rnd() * Math.PI * 2, ca = Math.cos(ang), sa = Math.sin(ang);
    const len = 0.25 + 0.2 * rnd(), bend = (rnd() - 0.5) * 0.3, N = 14;
    for (let f = 0; f < 4; f++) {
      const off = (f - 1.5) * 0.045, w = 0.011 + 0.003 * rnd();
      let prev = null;
      for (let k = 0; k <= N; k++) {
        const s = k / N, along = s * len, side = off + bend * s * s;
        const q = [p[0] + ca * along - sa * side, p[1] + sa * along + ca * side];
        if (prev) this._prim(TY.CAP, prev[0], prev[1], q[0], q[1], w * (1 - 0.3 * s), w * (1 - 0.3 * s - 0.3 / N), t + beatS * 0.5 * s + f * 0.01, col);
        prev = q;
      }
    }
    this._stats.hands++;
  }

  // --- le titre au pinceau (R21 : le trait central de la fonte) ------------------
  _textLine(font, str, cap, cx, base, maxW, radius, col, t, dur) {
    if (!font || !font.glyphs || !str) return t;
    let adv = 0;
    for (const ch of str) adv += font.advances[ch] != null ? font.advances[ch] : 0.5;
    if (adv <= 0) return t;
    cap = Math.min(cap, maxW / adv);
    const r = radius * cap;
    // longueur totale, pour répartir la durée
    const strokes = [];
    let x = cx - adv * cap / 2, len = 0;
    for (const ch of str) {
      const g = font.glyphs[ch];
      if (g) for (const st of g) {
        const pts = st.map(([px, py]) => [x + px * cap, base + py * cap]);
        for (let k = 1; k < pts.length; k++) len += Math.hypot(pts[k][0] - pts[k - 1][0], pts[k][1] - pts[k - 1][1]);
        strokes.push(pts);
      }
      x += (font.advances[ch] != null ? font.advances[ch] : 0.5) * cap;
    }
    const lift = 0.03;
    const speed = len / Math.max(0.2, dur - lift * strokes.length);
    const step = Math.max(0.006, r * 0.8);
    for (const pts of strokes) {
      const sd = this._rnd() * 100;
      let along = 0;
      for (let k = 1; k < pts.length; k++) {
        const a = pts[k - 1], b = pts[k];
        const L = Math.hypot(b[0] - a[0], b[1] - a[1]);
        const n = Math.max(1, Math.ceil(L / step));
        for (let j = 0; j < n; j++) {
          const s0 = j / n, s1 = (j + 1) / n;
          // le pinceau chargé en début de trait, qui s'appauvrit
          const w0 = r * (1.15 - 0.3 * Math.min(1, (along + L * s0) / 0.3)), w1 = r * (1.15 - 0.3 * Math.min(1, (along + L * s1) / 0.3));
          t += (L / n) / speed;
          this._prim(TY.BRUSH, a[0] + (b[0] - a[0]) * s0, a[1] + (b[1] - a[1]) * s0, a[0] + (b[0] - a[0]) * s1, a[1] + (b[1] - a[1]) * s1, w0, w1, t, col, sd);
        }
        along += L;
      }
      t += lift;
    }
    return t;
  }

  _writeText(t, beatS, instant) {
    const sans = getHersheySans(), script = getHersheyFont();
    if (!this._title || !sans) { this._textBox = null; return; }
    const a = this._aspect;
    const stage = safeZoneUniforms.uStageCenter.value, half = safeZoneUniforms.uStageHalfSize.value;
    const pal = this._palette(), rot = this._phraseN % 3;
    const cap = 0.13, capA = 0.075;
    // sous le bloc pochette au centre de composition (R19), au-dessus de la barre du bas
    const top = Math.max(-0.66, Math.min(-0.2, stage.y - half.y - 0.05));
    const base = Math.max(-0.80, top - cap);
    const baseA = base - 0.06 - capA;
    const T = this._title.toUpperCase().slice(0, 28);
    const dur = instant ? 0.001 : 5 * beatS;
    let te = this._textLine(sans, T, cap, stage.x, base, a * 1.1, 0.085, pal[rot % 3], t, dur);
    if (this._artist && script) te = this._textLine(script, this._artist.slice(0, 32), capA, stage.x, baseA, a * 0.7, 0.075, pal[(rot + 1) % 3], te + (instant ? 0 : beatS * 0.5), instant ? 0.001 : 3 * beatS);
    this._textBox = [stage.x - a * 0.6, baseA - capA * 0.6, stage.x + a * 0.6, base + cap * 1.25];
    this._textUntil = this._time + Math.max(12, (0.55 - this._clock.phase) * SCENE_S);
    this._stats.texts++;
  }

  // --- la scène ----------------------------------------------------------------
  _startWipe(now, beatS) {
    const a = this._aspect;
    this._wipePending = false;
    // le rouleau ouvre une nouvelle scène (fin de cycle, montée, changement de morceau)
    if (this._clock.phase > 0.02) this._clock.restart(0);
    this._cancelPending(now);
    this._blobs = []; this._recent = [];
    const dur = Math.max(0.3, beatS);
    const span = 2 * a + ROLLER_W + 0.04;
    this._wipe = { t0: now, dur, span };
    // derrière le rouleau, des bandes noires
    const n = 24;
    for (let i = 0; i < n; i++) {
      const x0 = -a - 0.01 + (2 * a + 0.02) * i / n, x1 = -a - 0.01 + (2 * a + 0.02) * (i + 1) / n;
      const k = (x1 + 0.025 + a) / span;
      this._prim(TY.RECT, (x0 + x1) / 2, 0, (x1 - x0) / 2 + 0.004, 1.05, 0, 0, now + dur * Math.min(1, k), [0, 0, 0]);
    }
    this._clearAt = now + dur;
    this._textDue = now + dur + beatS * 0.25;
    this._textBox = null;
    this._stats.wipes++;
  }

  _requestWipe() { if (!this._wipe && !this._wipePending) { this._wipePending = true; this._wipeAsked = this._time; } }

  // ouvre sur un mur déjà vivant : la phase tirée de la scène, pré-remplie en
  // quelques images (les instants sont tassés juste après maintenant)
  _prefill(beatS) {
    const ph = this._clock.phase, now = this._time;
    let t = now + 0.001;
    if (this._title) { this._writeText(t, beatS, true); t += 0.002; }
    else this._textDue = now;
    if (ph < 0.04) return;
    const beats = Math.min(90, Math.floor(ph * SCENE_S / beatS * 0.8));
    for (let k = 0; k < beats; k++) {
      this._beatSplat(t, 1, k < beats * 0.25); t += 0.0005;
      if (k % 4 === 3) {
        if (this._rnd() < 0.4) { this._jet(t, 0.0001); t += 0.0005; }
        if (this._drips && this._blobs.length) { this._drip(this._blobs[this._blobs.length - 1], t, true); t += 0.0005; }
      }
    }
  }

  _hasTitleChanged(profile) {
    const ti = plain(profile.title), ar = plain(profile.artist);
    const key = ti + '|' + ar;
    if (key === this._textKey) return false;
    this._textKey = key; this._title = ti; this._artist = ar;
    return true;
  }

  update(deltaTime, profile) {
    const paused = profile.playbackState !== 'PLAYING';
    this._paused = paused;
    const dt = paused ? 0 : Math.min(deltaTime, 0.05);
    const a = this._adapt.update(dt, profile);
    this._aspect = safeZoneUniforms.uAspect.value || 16 / 9;
    if (profile.artworkPalette !== this._paletteRef) { this._paletteRef = profile.artworkPalette; this._applyPalette(profile.artworkPalette); }
    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || 124;
    const beatS = 60 / bpm, barS = 4 * beatS;
    const changed = this._hasTitleChanged(profile);
    if (paused) return;                                          // plus rien n'est peint, l'image tient
    this._time += dt;
    this._clock.update(dt);
    const now = this._time;
    if (!this._started) { this._started = true; this._prefill(beatS); }
    else if (changed) this._requestWipe();                         // nouveau morceau : nouvelle scène

    const director = profile.director || null;
    const next = director && director.nextEvent;
    let build = 0;
    if (next && next.type === 'drop' && next.msUntil < 8000) build = 1 - next.msUntil / 8000;
    if (director && director.mode === 'build') build = Math.max(build, 0.6);
    this._build += (build - this._build) * (1 - Math.exp(-dt / 0.8));
    const inten = a.intensity != null ? a.intensity : 0.7;
    const calm = inten < 0.3 && !this._dbgSplat;
    const early = this._clock.phase < 0.25;
    const scale = (0.6 + 0.6 * inten) * (1 - 0.55 * this._build);
    const bp = a.beatPhase != null ? a.beatPhase : clamp01(this._beatAge / beatS);

    if (this._clock.wrapped || (this._dbgWipe && a.bar && this._bars % 2 === 1)) this._requestWipe();
    // une montée en fin de scène : le rouleau passe tout de suite, le drop explosera sur un mur propre
    if (this._build > 0.3 && this._clock.phase > 0.75 && this._dropAge > 4 * barS) this._requestWipe();
    // jamais dans les 4 mesures qui suivent un drop (au plus 16 mesures d'attente)
    const wipeOk = this._dropAge > 4 * barS || now - this._wipeAsked > 16 * barS;
    if (this._wipePending && wipeOk && now - this._wipeAsked > 1.0) this._startWipe(now, beatS);   // sans temps : on n'attend pas

    if (a.beat) {
      this._beats++; this._beatAge = 0; this._halfDone = false;
      if (this._wipePending && wipeOk) this._startWipe(now, beatS);
      else if (!this._wipe) {
        if (!calm || this._beats % 4 === 0) this._beatSplat(now, scale, early);
        if (this._dbgDrip && this._drips && this._blobs.length > 1) this._drip(this._blobs[this._blobs.length - 2], now);
      }
    }
    this._beatAge += dt;
    // croches : en montée (et sous ?blsplat), une éclaboussure au demi-temps
    if (!this._halfDone && bp >= 0.5 && this._beatAge < beatS) {
      this._halfDone = true;
      if (!this._wipe && !(this._wipePending && wipeOk) && (this._build > 0.35 || this._dbgSplat)) this._beatSplat(now, scale * 0.8, early);
    }
    if (a.bar) {
      this._bars++;
      if (!this._wipe && !(this._wipePending && wipeOk) && !calm) {
        this._jet(now, beatS);
        if (this._drips) {
          const n = 1 + Math.floor(this._rnd() * 3);
          for (let k = 0; k < n && k < this._blobs.length; k++) {
            const b = this._blobs[this._blobs.length - 1 - k];
            if (b.t < now - 0.1) this._drip(b, now + k * 0.15);
          }
        }
        const h = this._rnd();
        if (h < 0.08) this._hand(now + beatS * 0.5);
        else if (h < 0.16) this._fingers(now + beatS * 0.5, beatS);
      }
    }
    if (a.phrase) this._phraseN++;
    if (a.drop) {
      this._dropAge = 0;
      if (!this._wipe) {
        // huit éclaboussures depuis le centre de composition
        const st = safeZoneUniforms.uStageCenter.value, rot = this._rnd() * Math.PI * 2;
        for (let k = 0; k < 8; k++) {
          const ang = rot + k * Math.PI / 4 + (this._rnd() - 0.5) * 0.3, rr = 0.3 + 0.25 * this._rnd();
          this._splat(st.x + Math.cos(ang) * rr * 1.2, st.y + Math.sin(ang) * rr * (this._time < this._textUntil ? 0.7 : 1), 0.10 + 0.05 * this._rnd(), ang, this._palette()[k % 3], now + k * 0.004);
        }
      }
      this._uvFlash = 0.45;
      if (director && this._comfort.requestFlash(this._time)) director.flash = VDJ_DROP_FLASH;
    }
    this._dropAge += dt;

    // le rouleau
    let rx = -99;
    if (this._wipe) {
      const k = (now - this._wipe.t0) / this._wipe.dur;
      if (k >= 1) this._wipe = null;
      else rx = -this._aspect - 0.02 + this._wipe.span * k;
    }
    this._u.uRollerX.value = rx;
    if (this._textDue >= 0 && now >= this._textDue) {
      this._textDue = -1;
      this._writeText(now, beatS, false);
    }

    // la lumière noire : stroboscope des montées (un flash par temps au plus), flash UV du drop
    let uv = 1;
    const sb = clamp01((this._build - 0.35) / 0.3);
    if (sb > 0) uv = bp < 0.45 ? 1 : 1 - sb;
    this._uvFlash *= Math.exp(-dt / 0.12);
    uv += this._uvFlash;
    this._uv = uv;
    this._u.uUV.value = uv;
    this._move(dt, a, bp, beatS, barS, inten);
    if (this._lumFresh) { this._lumFresh = false; this._gain = this._comfort.measure(this._lumMeasured, 0.5); }
    this._lumAge += dt;
    this._u.uGain.value = this._gain;
    this._headU.uTime.value = now;
    this._headU.uHeadGain.value = (0.7 + 0.3 * Math.min(uv, 1.4)) * this._gain;
    if (this._pdirty) { this._pdirty = false; this._ptex.needsUpdate = true; }
    this._post.update(vdjBeatChroma(bp, beatS));
  }

  // LE MOUVEMENT (2026-10-23, « trop statique ») - rien n'est redessiné dans le tampon (R22) : la caméra bouge
  // sur le mur, deux lumières noires mobiles le balaient, la fluorescence respire sur le temps.
  _move(dt, a, bp, beatS, barS, inten) {
    const t = this._time, asp = this._aspect;
    if (!this._mv) this._mv = { push: 0, sway: this._rnd() * 6.28, kick: 0, spots: [0, 1].map((i) => ({ ph: this._rnd() * 6.28, fx: 0.11 + 0.05 * this._rnd(), fy: 0.07 + 0.04 * this._rnd(), dir: i ? -1 : 1 })) };
    const m = this._mv;
    // the camera: a push in over the scene (the wall fills up, we come closer), a slow sway, a kick on the beat
    const kick = Math.pow(1 - clamp01(bp), 3) * Math.max(0.3, a.pulse != null ? a.pulse : 0.6);
    m.kick += (kick - m.kick) * Math.min(1, dt / 0.03);
    const push = 0.02 + 0.07 * this._clock.phase;
    const zoom = 1.03 + push + 0.022 * m.kick * (0.6 + 0.6 * inten) + 0.04 * this._build;
    this._u.uZoom.value = zoom;
    const margin = (1 - 1 / zoom);                   // how far the view may pan (height units, both axes) without an edge
    m.sway += dt * (0.18 + 0.25 * inten);
    this._u.uPan.value.set(margin * asp * 0.85 * Math.sin(m.sway), margin * 0.85 * Math.sin(m.sway * 0.71 + 1.3));
    // the UV lights: Lissajous sweeps across the wall, faster with the energy, reversed on every bar; a strong sweep in the builds
    if (a.bar) for (const s of m.spots) { s.dir = -s.dir; s.fx = 0.10 + 0.08 * this._rnd(); }
    for (let i = 0; i < 2; i++) {
      const s = m.spots[i];
      s.ph += dt * s.dir * (0.5 + 0.9 * inten + 1.2 * this._build);
      const x = Math.sin(s.ph * s.fx * 6.28 + i * 3.1) * asp * 0.85, y = Math.sin(s.ph * s.fy * 6.28 + i * 1.7) * 0.75;
      this._u.uSpot.value[i].set(x, y, 0.55 + 0.25 * inten, 0.75 + 0.35 * kick);
    }
    // the fluorescence breathes on the beat (+18 % on the kick), under the comfort gain
    this._u.uPulse.value = 0.92 + 0.18 * kick;
  }

  _ensureBuffers(renderer, w, h) {
    if (this._paintRT && this._bw === w && this._bh === h) return;
    for (const t of [this._paintRT, this._glowA, this._glowB]) if (t) t.dispose();
    const opt = { type: THREE.UnsignedByteType, minFilter: THREE.LinearFilter, magFilter: THREE.LinearFilter, depthBuffer: false, generateMipmaps: false };
    this._paintRT = new THREE.WebGLRenderTarget(w, h, opt);
    this._glowA = new THREE.WebGLRenderTarget(Math.max(1, Math.ceil(w / 4)), Math.max(1, Math.ceil(h / 4)), opt);
    this._glowB = new THREE.WebGLRenderTarget(Math.max(1, Math.ceil(w / 16)), Math.max(1, Math.ceil(h / 16)), opt);
    this._bw = w; this._bh = h;
    this._u.uPaint.value = this._paintRT.texture; this._u.uGlowA.value = this._glowA.texture; this._u.uGlowB.value = this._glowB.texture;
    this._needClear = true;
  }

  _render(renderer) {
    // la taille de la CIBLE courante (la passe de rendu du composer), pas du canvas
    const prev = renderer.getRenderTarget();
    if (prev) _size.set(prev.width, prev.height); else renderer.getDrawingBufferSize(_size);
    const w = Math.max(2, _size.x), h = Math.max(2, _size.y);
    this._ensureBuffers(renderer, w, h);
    renderer.getClearColor(_cc); const ca = renderer.getClearAlpha();
    const autoClear = renderer.autoClear;
    renderer.autoClear = false;
    renderer.setClearColor(0x000000, 1);
    const T0 = this._drawnT, T1 = this._paused ? T0 : this._time;
    let painted = false;
    renderer.setRenderTarget(this._paintRT);
    if (this._needClear || (this._clearAt > T0 && this._clearAt <= T1)) {
      // R22 : à la sortie du rouleau, le tampon revient au noir exact
      renderer.clear(true, false, false);
      this._needClear = false; painted = true;
      if (this._clearAt <= T1) this._clearAt = -1;
    }
    if (T1 > T0 && this._maxT > T0) {
      this._paintU.uT0.value = T0; this._paintU.uT1.value = T1;
      this._paintU.uAspect.value = w / h; this._paintU.uPx.value = 2 / h;
      renderer.render(this._paintScene, this._ortho);
      painted = true;
    }
    this._drawnT = T1;
    if (painted) {
      this._downU.uSrc.value = this._paintRT.texture; this._downU.uTexel.value.set(1 / w, 1 / h);
      renderer.setRenderTarget(this._glowA); renderer.render(this._downScene, this._ortho);
      this._downU.uSrc.value = this._glowA.texture; this._downU.uTexel.value.set(1 / this._glowA.width, 1 / this._glowA.height);
      renderer.setRenderTarget(this._glowB); renderer.render(this._downScene, this._ortho);
    }
    if (this._lumAge >= 0.5 && !this._paused) {
      this._lumAge = 0;
      this._u.uRes.value.set(32, 32);
      renderer.setRenderTarget(this._measRT);
      renderer.clear(true, false, false);
      renderer.render(this._measScene, this._ortho);
      try {
        renderer.readRenderTargetPixels(this._measRT, 0, 0, 32, 32, this._measBuf);
        let sum = 0;
        for (let i = 0; i < this._measBuf.length; i += 4) sum += 0.2126 * this._measBuf[i] + 0.7152 * this._measBuf[i + 1] + 0.0722 * this._measBuf[i + 2];
        this._lumMeasured = sum / (32 * 32 * 255);
        this._lumFresh = true;
      } catch (e) { /* la prochaine fois */ }
    }
    this._u.uRes.value.set(w, h);
    renderer.setClearColor(_cc, ca);
    renderer.setRenderTarget(prev);
    renderer.autoClear = autoClear;
  }

  getProbe() {
    return {
      t: +this._time.toFixed(2), scene: +this._clock.phase.toFixed(3), beats: this._beats, bars: this._bars, phrase: this._phraseN,
      written: this._written, ...this._stats, dripsOn: this._drips, wipe: !!this._wipe, pending: this._wipePending, text: this._title ? this._title.slice(0, 20) : '',
      buffer: this._bw + 'x' + this._bh, build: +this._build.toFixed(3), uv: +this._uv.toFixed(2),
      lum: +this._lumMeasured.toFixed(3), gain: +this._gain.toFixed(3)
    };
  }

  dispose() {
    if (this._quad) { this._quad.onBeforeRender = null; if (this._scene) this._scene.remove(this._quad); this._quad.geometry.dispose(); this._quad.material.dispose(); }
    if (this._heads) { if (this._scene) this._scene.remove(this._heads); this._heads.geometry.dispose(); this._heads.material.dispose(); }
    for (const m of [this._paintMesh, this._downMesh]) if (m) { m.geometry.dispose(); m.material.dispose(); }
    for (const t of [this._paintRT, this._glowA, this._glowB, this._measRT]) if (t) t.dispose();
    for (const t of [this._ptex, this._dtex]) if (t) t.dispose();
    if (this._adapt) this._adapt.dispose();
    this._scene = null;
  }
}

const _size = new THREE.Vector2();
const _cc = new THREE.Color();
